import confetti from 'canvas-confetti';

import { useUIStore } from '@/stores/uiStore';

// SplitFiesta brand colors for confetti
const BRAND_COLORS = ['#ff6b35', '#f7931e', '#ffd700', '#00d4aa', '#00a8cc'];

/**
 * Triggers a celebration confetti effect with different intensity levels
 * @param intensity - 'subtle' for small celebrations, 'medium' for standard, 'high' for major celebrations
 *
 * Honors the user's `celebrationsEnabled` preference from `useUIStore`
 * (persisted across sessions, settable in Settings). When disabled
 * this becomes a no-op — call sites don't have to gate themselves.
 * Reads via `getState()` so it works both in React components and in
 * non-React code paths (mutation callbacks, etc.) without needing a
 * subscription.
 */
export function celebrate(
  intensity: 'subtle' | 'medium' | 'high' = 'medium'
): void {
  if (!useUIStore.getState().celebrationsEnabled) return;
  switch (intensity) {
    case 'subtle':
      // Single burst for minor actions (updates, small achievements)
      confetti({
        particleCount: 50,
        spread: 45,
        origin: { y: 0.7 },
        colors: BRAND_COLORS,
      });
      break;

    case 'medium':
      // Standard celebration (creating expenses, completing tasks)
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: BRAND_COLORS,
      });

      // Add side bursts for extra impact
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: BRAND_COLORS,
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: BRAND_COLORS,
        });
      }, 200);
      break;

    case 'high':
      // Major celebration (joining groups, big achievements)
      // Multiple rapid-fire bursts for maximum impact

      // Initial center burst
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: BRAND_COLORS,
      });

      // Quick follow-up bursts from corners
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: BRAND_COLORS,
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: BRAND_COLORS,
        });
      }, 100);

      // Side bursts
      setTimeout(() => {
        confetti({
          particleCount: 75,
          angle: 45,
          spread: 70,
          origin: { x: 0, y: 0.7 },
          colors: BRAND_COLORS.slice(0, 3),
        });
        confetti({
          particleCount: 75,
          angle: 135,
          spread: 70,
          origin: { x: 1, y: 0.7 },
          colors: BRAND_COLORS.slice(2),
        });
      }, 200);

      // More center bursts
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 100,
          startVelocity: 30,
          origin: { y: 0.5 },
          colors: BRAND_COLORS,
        });
      }, 300);

      // Final cascade from top
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 120,
          origin: { y: 0 },
          gravity: 0.3,
          colors: BRAND_COLORS,
        });
      }, 400);

      // Extra shower effect
      setTimeout(() => {
        confetti({
          particleCount: 50,
          spread: 180,
          origin: { y: 0.2 },
          gravity: 0.5,
          colors: BRAND_COLORS,
        });
      }, 500);
      break;
  }
}
